import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { recordedAt } from "@/lib/metrics/history";

const db = supabase as any;

export type Period = "weekly" | "biweekly" | "semimonthly" | "monthly";
export interface IncomeRecord {
  id: string; user_id: string; kind: "w2" | "paystub" | "manual" | "payout" | "1099"; source_id?: string | null;
  tax_year: number | null; pay_date: string | null; period: Period | null;
  gross: number; base: number; overtime: number; bonus: number; variable: number;
  source_note: string | null; created_at: string;
}
export const PER_YEAR: Record<Period, number> = { weekly: 52, biweekly: 26, semimonthly: 24, monthly: 12 };

export interface IncomeProfile {
  annual: number | null;
  reliableMonthly: number | null;
  mix: { base: number; overtime: number; bonus: number; variable: number };
  stability: "Steady" | "Mostly steady" | "Variable" | null;
  w2: { year: number; gross: number }[];
  source: "paystubs" | "w2" | null;
  averageMonthly?: number | null;
  months?: { label: string; parts: Record<string, number> }[];
  streams?: { id: string; name: string; kind: string; monthly: number; reliable: number; lumpy: boolean; lastPaid: string | null }[];
}

export type SourceKind = "job" | "covered_calls" | "rental" | "other";
export interface IncomeSource {
  id: string; user_id: string; kind: SourceKind; name: string; line_of_work: string | null;
  pay_type: "w2" | "1099" | "owner" | null; structure: "base" | "base_commission" | "commission" | "base_extras" | null;
  rent: number | null; costs: number | null; created_at: string;
}
/** Sources paid by payouts (no regular paycheck). */
export const isLumpySource = (s: IncomeSource) => s.kind === "covered_calls" || s.kind === "other" || (s.kind === "job" && (s.pay_type !== "w2" || s.structure === "commission"));

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
/** Adds payout sources and rentals on top of the paycheck profile. Lumpy income counts at half its 12-month average (empty months = $0). */
export function computeFullProfile(rows: IncomeRecord[], sources: IncomeSource[]): IncomeProfile {
  const lumpyIds = new Set(sources.filter(isLumpySource).map((s) => s.id));
  const pay = computeIncomeProfile(rows.filter((r) => !r.source_id || !lumpyIds.has(r.source_id)).filter((r) => r.kind !== "payout" && r.kind !== "1099"));
  const now = new Date();
  const keys = Array.from({ length: 12 }, (_, i) => monthKey(new Date(now.getFullYear(), now.getMonth() - 11 + i, 1)));
  const months = keys.map((k) => ({ label: new Date(k + "-01T12:00").toLocaleDateString(undefined, { month: "short" }), parts: {} as Record<string, number> }));
  const payM = (pay.annual ?? 0) / 12, payR = pay.reliableMonthly ?? 0;
  if (payM) months.forEach((m) => (m.parts["Paycheck"] = payM));
  const streams: NonNullable<IncomeProfile["streams"]> = [];
  for (const s of sources) {
    if (s.kind === "rental") {
      const net = Math.max(0, (Number(s.rent) || 0) - (Number(s.costs) || 0));
      months.forEach((m) => (m.parts[s.name] = (m.parts[s.name] ?? 0) + net));
      streams.push({ id: s.id, name: s.name, kind: s.kind, monthly: net, reliable: net, lumpy: false, lastPaid: null });
      continue;
    }
    if (!isLumpySource(s)) continue;
    const mine = rows.filter((r) => r.source_id === s.id);
    const payouts = mine.filter((r) => r.kind === "payout" && r.pay_date);
    let total = 0;
    for (const r of payouts) { const i = keys.indexOf(r.pay_date!.slice(0, 7)); if (i >= 0) { months[i].parts[s.name] = (months[i].parts[s.name] ?? 0) + Number(r.gross); total += Number(r.gross); } }
    let avg = total / 12;
    if (!payouts.length) {
      const yrs = mine.filter((r) => r.kind === "1099" && r.tax_year).sort((a, b) => b.tax_year! - a.tax_year!);
      if (yrs.length) avg = Math.min(...yrs.slice(0, 2).map((r) => Number(r.gross))) / 12;
    }
    const last = payouts.map((r) => r.pay_date!).sort().pop() ?? null;
    streams.push({ id: s.id, name: s.name, kind: s.kind, monthly: avg, reliable: avg * 0.5, lumpy: true, lastPaid: last });
  }
  const extraM = streams.reduce((a, x) => a + x.monthly, 0), extraR = streams.reduce((a, x) => a + x.reliable, 0);
  const avgM = payM + extraM;
  if (!avgM) return { ...pay, averageMonthly: null, months, streams };
  return { ...pay, annual: avgM * 12, reliableMonthly: payR + extraR, averageMonthly: avgM, months, streams, stability: streams.some((x) => x.lumpy && x.monthly > payM * 0.25) ? "Variable" : pay.stability };
}

const median = (a: number[]) => { const s = [...a].sort((x, y) => x - y); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };

/** Annualized income from the last 6 months of stubs (else latest W-2). Reliable = base + median of extras, never the best check. */
export function computeIncomeProfile(rows: IncomeRecord[]): IncomeProfile {
  const w2 = rows.filter((r) => r.kind === "w2" && r.tax_year).map((r) => ({ year: r.tax_year!, gross: Number(r.gross) })).sort((a, b) => a.year - b.year);
  const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() - 6);
  const stubs = rows.filter((r) => r.kind !== "w2" && r.period && r.pay_date && new Date(r.pay_date) >= cutoff);
  const mix = { base: 0, overtime: 0, bonus: 0, variable: 0 };
  if (stubs.length) {
    const yr = (r: IncomeRecord, k: keyof typeof mix | "gross") => Number(r[k]) * PER_YEAR[r.period!];
    for (const k of Object.keys(mix) as (keyof typeof mix)[]) mix[k] = stubs.reduce((a, r) => a + yr(r, k), 0) / stubs.length;
    const annual = stubs.reduce((a, r) => a + yr(r, "gross"), 0) / stubs.length;
    const extras = stubs.map((r) => yr(r, "overtime") + yr(r, "bonus") + yr(r, "variable"));
    const baseAnnual = median(stubs.map((r) => yr(r, "base")));
    const reliable = Math.min(annual, baseAnnual + median(extras));
    const share = annual ? mix.base / annual : 1;
    return { annual, reliableMonthly: reliable / 12, mix, w2, source: "paystubs", stability: share >= 0.85 ? "Steady" : share >= 0.6 ? "Mostly steady" : "Variable" };
  }
  const last = w2[w2.length - 1];
  if (last) {
    // W-2 can't split pay types; trust the lower of the last two years.
    const prev = w2[w2.length - 2];
    const reliable = prev ? Math.min(prev.gross, last.gross) : last.gross;
    return { annual: last.gross, reliableMonthly: reliable / 12, mix, w2, source: "w2", stability: null };
  }
  return { annual: null, reliableMonthly: null, mix, w2, source: null, stability: null };
}

export function useIncomeRecords(userId?: string) {
  const { user } = useAuth();
  const uid = userId ?? user?.id;
  return useQuery({
    queryKey: ["income-records", uid],
    enabled: !!uid,
    queryFn: async (): Promise<IncomeRecord[]> => {
      const { data, error } = await db.from("income_records").select("*").eq("user_id", uid).order("pay_date", { ascending: false, nullsFirst: false }).order("tax_year", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useIncomeSources(userId?: string) {
  const { user } = useAuth();
  const uid = userId ?? user?.id;
  return useQuery({
    queryKey: ["income-sources", uid],
    enabled: !!uid,
    queryFn: async (): Promise<IncomeSource[]> => {
      const { data, error } = await db.from("income_sources").select("*").eq("user_id", uid).order("created_at");
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useIncomeProfile(userId?: string) {
  const q = useIncomeRecords(userId);
  const s = useIncomeSources(userId);
  return { ...q, sources: s.data ?? [], profile: computeFullProfile(q.data ?? [], s.data ?? []) };
}

export function useSaveIncomeSource() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const done = () => { qc.invalidateQueries({ queryKey: ["income-sources"] }); qc.invalidateQueries({ queryKey: ["income-records"] }); qc.invalidateQueries({ queryKey: ["kingdom-path-inputs"] }); };
  return {
    save: useMutation({
      mutationFn: async (r: Partial<IncomeSource> & { name: string; kind: SourceKind }) => {
        const row = { ...r, user_id: user!.id };
        const { error } = r.id ? await db.from("income_sources").update(row).eq("id", r.id) : await db.from("income_sources").insert(row);
        if (error) throw error;
      },
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => { const { error } = await db.from("income_sources").delete().eq("id", id); if (error) throw error; },
      onSuccess: done,
    }),
  };
}

export function useSaveIncome() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const done = () => { qc.invalidateQueries({ queryKey: ["income-records"] }); qc.invalidateQueries({ queryKey: ["kingdom-path-inputs"] }); };
  return {
    save: useMutation({
      mutationFn: async (r: Partial<IncomeRecord> & { kind: IncomeRecord["kind"]; gross: number }) => {
        if (!user) throw new Error("Not signed in");
        if (["paystub", "manual", "payout"].includes(r.kind)) recordedAt(r.pay_date ?? "");
        for (const k of ["gross", "base", "overtime", "bonus", "variable"] as const) if (r[k] != null && (!Number.isFinite(r[k]) || Number(r[k]) < 0)) throw new Error("Check each income amount");
        if (["w2", "1099"].includes(r.kind) && (!Number.isInteger(r.tax_year) || Number(r.tax_year) < 1990 || Number(r.tax_year) > new Date().getFullYear())) throw new Error("Enter a real tax year");
        const row = { kind:r.kind, gross:r.gross, base:r.base??0, overtime:r.overtime??0, bonus:r.bonus??0, variable:r.variable??0, pay_date:r.pay_date??null, tax_year:r.tax_year??null, period:r.period??null, source_id:r.source_id??null };
        const { error } = r.id ? await db.from("income_records").update(row).eq("id", r.id).eq("user_id",user.id).select("id").single() : await db.from("income_records").insert({...row,user_id:user.id});
        if (error) throw error;
      },
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => { const { error } = await db.from("income_records").delete().eq("id", id); if (error) throw error; },
      onSuccess: done,
    }),
  };
}
